import { criterion, fail, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { dateiendung } from "@/office/excel/analyse";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_MAKROS } from "../makros";

export const lektionMakros: Lesson = {
  id: "makros",
  title: "Makros aufzeichnen und speichern",
  summary: "Wiederkehrende Handgriffe automatisieren – mit relativen Bezügen und in der richtigen Datei.",
  durationMinutes: 12,
  learningGoals: [
    "Ein Makro mit dem Makrorekorder aufzeichnen",
    "Relative und absolute Aufzeichnung unterscheiden",
    "Makros in einer .xlsm-Datei oder der persönlichen Makroarbeitsmappe speichern",
  ],
  steps: [
    {
      id: "aufzeichnen",
      title: "Ein Makro aufzeichnen",
      explanation: [
        "„Ansicht“ → „Makros“ → „Makro aufzeichnen“ (oder die Registerkarte " +
          "„Entwicklertools“). Name vergeben, Speicherort wählen: „Diese Arbeitsmappe“ oder " +
          "„Persönliche Makroarbeitsmappe“ – letztere ist die Datei PERSONAL.XLSB, die bei " +
          "jedem Excel-Start im Hintergrund geöffnet wird. Dann ausführen, was aufgezeichnet " +
          "werden soll, und „Aufzeichnung beenden“.",
        "Standardmäßig zeichnet Excel absolute Zelladressen auf: Das Makro formatiert immer " +
          "A1, egal wo der Cursor steht. „Relative Verweise verwenden“ (vor dem Aufzeichnen " +
          "einschalten) macht daraus „eine Zelle rechts vom Cursor“ – für die meisten " +
          "Formatierungsmakros die richtige Wahl.",
        "Wie bei Word gilt: Makros nur aus bekannter Quelle aktivieren. Die Prüfmakros " +
          "dieses Kurses sind lesbar und lesen nur – ein guter Anlass, sich den Code vor dem " +
          "Ausführen anzusehen.",
      ],
      keyPoints: [
        "Ansicht → Makros → Makro aufzeichnen.",
        "Relative Verweise vor dem Aufzeichnen einschalten.",
        "PERSONAL.XLSB = überall verfügbar.",
      ],
      task: {
        instruction:
          "Zeichne ein Makro namens „Kursmakro“ auf, das etwa die markierte Zelle fett und gelb hinterlegt.",
        hint: "Ansicht → Makros → Makro aufzeichnen → Name „Kursmakro“ → Strg + Umschalt + F → … → Aufzeichnung beenden.",
        macro: MAKRO_MAKROS,
        check: () =>
          pruefeMitMakro(MAKRO_MAKROS, (bericht) => {
            if (bericht.janein("vbZugriff") !== true) {
              return unsupported(
                "Um aufgezeichnete Makros zu finden, muss das Prüfmakro den VBA-Code lesen – dafür ist " +
                  "„Zugriff auf das VBA-Projektobjektmodell vertrauen“ nötig, das hier ausgeschaltet ist.",
                "Datei → Optionen → Trust Center → Einstellungen → Makroeinstellungen → Häkchen setzen " +
                  "und das Prüfmakro erneut ausführen. Ist die Einstellung gesperrt, prüfe unter Alt + F8, " +
                  "ob „Kursmakro“ in der Liste steht, und markiere den Schritt selbst als erledigt.",
              );
            }
            return fromCriteria(
              [criterion("Ein Makro namens „Kursmakro“ ist aufgezeichnet", bericht.janein("kursmakro") === true)],
              {
                success: `„Kursmakro“ gefunden in ${bericht.text("ort") ?? "?"}.`,
                failure: "Ein Makro namens „Kursmakro“ wurde nicht gefunden.",
              },
            );
          }),
      },
    },
    {
      id: "als-xlsm",
      title: "Als Arbeitsmappe mit Makros speichern",
      explanation: [
        "Eine .xlsx-Datei kann keine Makros enthalten. Beim Speichern warnt Excel und bietet " +
          "den Dateityp „Excel-Arbeitsmappe mit Makros (*.xlsm)“ an. Makros in der " +
          "persönlichen Makroarbeitsmappe brauchen das nicht – sie reisen aber auch nicht mit " +
          "der Datei.",
      ],
      keyPoints: [".xlsx ohne, .xlsm mit Makros.", "PERSONAL.XLSB bleibt auf dem eigenen Rechner."],
      task: {
        instruction: "Speichere deine Übungsmappe als „Excel-Arbeitsmappe mit Makros (*.xlsm)“.",
        hint: "Datei → Speichern unter → Dateityp „Excel-Arbeitsmappe mit Makros“.",
        check: async () => {
          const endung = dateiendung();
          if (endung === null) {
            return fail("Die Arbeitsmappe ist noch nicht gespeichert.", "Speichere sie als .xlsm und prüfe erneut.");
          }
          return fromCriteria([criterion(`Dateityp .xlsm (aktuell: .${endung})`, endung === "xlsm")], {
            success: "Gespeichert als .xlsm – die Makros reisen mit der Datei.",
            failure: "Die Mappe ist noch nicht als .xlsm gespeichert.",
          });
        },
      },
    },
  ],
};
